/**
 * Responsibility: Implement high-quality adaptive audio behavior using ToneActual.js.
 * Scope: Owns audio synthesis, effects, and musical scheduling.
 */
import * as ToneActual from 'tone'

// Support global mock for unit tests where module mocking is difficult
const Tone = (globalThis as any).ToneMock || ToneActual

export type SoundEffect =
  | 'action_success'
  | 'action_fail'
  | 'action_partial'
  | 'item_pickup'
  | 'item_drop'
  | 'narrator_chime'
  | 'ui_click'
  | 'ui_open'
  | 'ui_close'
  | 'entropy_shift'
  | 'level_up'
  | 'danger'

export type AmbientScene =
  | 'dungeon'
  | 'forest'
  | 'tavern'
  | 'city'
  | 'wilderness'
  | 'void'
  | 'cave'
  | 'ocean'

export type ScoreMood = 'calm' | 'tension' | 'combat' | 'discovery' | 'triumph' | 'sorrow'

export type ToneSpec = {
  at: number
  hz: number
  duration: number
  gain: number
  wave?: OscillatorType
  glideTo?: number
}

const STORAGE_ENABLED = 'am2_audio_enabled'
const STORAGE_VOLUME = 'am2_audio_volume'
const DEFAULT_VOLUME = 0.85

// --- Global State ---
let enabled = readEnabled()
let volume = readVolume()
let audioUnlocked = false
let initialized = false
let initListenersBound = false
let toneStartPromise: Promise<void> | null = null
let lastStartReason = 'never'

// --- Signal Chain ---
let masterLimiter: ToneActual.Limiter | null = null
let globalReverb: ToneActual.Reverb | null = null
let effectBus: ToneActual.Gain | null = null
let scoreBus: ToneActual.Gain | null = null
let ambientBus: ToneActual.Gain | null = null

// --- Instruments ---
let melodySynth: ToneActual.PolySynth<ToneActual.Synth> | null = null
let bassSynth: ToneActual.MonoSynth | null = null
let harmonySynth: ToneActual.PolySynth<ToneActual.Synth> | null = null
let sfxSynth: ToneActual.PolySynth<ToneActual.Synth> | null = null

// --- Tracking ---
let currentAmbientScene: AmbientScene | null = null
let desiredAmbientScene: AmbientScene | null = null
let scoreMood: ScoreMood | null = null
let dynamicScores = new Map<ScoreMood, Array<{ notes: ToneSpec[], durationMs: number }>>()
let lastScorePhraseIndex = -1
let nextScoreTransportId: number | null = null

// --- Initialization ---

function readEnabled(): boolean {
  try { return localStorage.getItem(STORAGE_ENABLED) !== 'false' } catch { return true }
}

function readVolume(): number {
  try {
    const stored = localStorage.getItem(STORAGE_VOLUME)
    if (stored === null) return DEFAULT_VOLUME
    const raw = Number(stored)
    return Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : DEFAULT_VOLUME
  } catch { return DEFAULT_VOLUME }
}

async function ensureToneStarted(reason = 'unknown') {
  lastStartReason = reason
  if (toneStartPromise) {
    await toneStartPromise
    return
  }

  if (!enabled && !initialized) return

  if (initialized) {
    if (ToneActual.getContext().state !== 'running') {
      await ToneActual.getContext().resume()
    }
    return
  }
  if (!audioUnlocked) return

  toneStartPromise = (async () => {
    await ToneActual.start()
    
    // 1. Setup Busses
    masterLimiter = new ToneActual.Limiter(-3).toDestination()
    
    globalReverb = new ToneActual.Reverb({ decay: 2.5, preDelay: 0.05, wet: 0.25 }).connect(masterLimiter)
    // AUDIO-BUG-04: Don't await generate() to avoid blocking init, it will generate in background
    globalReverb.generate().catch(e => console.warn('Reverb generation failed', e))

    effectBus = new ToneActual.Gain(1.0).connect(masterLimiter)
    effectBus.connect(globalReverb)

    scoreBus = new ToneActual.Gain(0.2).connect(masterLimiter)
    scoreBus.connect(globalReverb)

    ambientBus = new ToneActual.Gain(0.12).connect(masterLimiter)

    // 2. Setup Instruments
    melodySynth = new ToneActual.PolySynth(ToneActual.Synth, {
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.05, decay: 0.2, sustain: 0.4, release: 0.8 }
    }).connect(scoreBus)

    bassSynth = new ToneActual.MonoSynth({
      oscillator: { type: 'fmsawtooth', modulationIndex: 2, modulationType: 'sine' },
      envelope: { attack: 0.1, decay: 0.3, sustain: 0.6, release: 1.2 },
      filter: { Q: 1, type: 'lowpass', rolloff: -24 },
      filterEnvelope: { attack: 0.1, decay: 0.2, sustain: 0.5, release: 1, baseFrequency: 100, octaves: 2 }
    }).connect(scoreBus)

    harmonySynth = new ToneActual.PolySynth(ToneActual.Synth, {
      oscillator: { type: 'sine' },
      envelope: { attack: 0.4, decay: 0.4, sustain: 0.8, release: 1.5 }
    }).connect(scoreBus)

    sfxSynth = new ToneActual.PolySynth(ToneActual.Synth, {
      oscillator: { type: 'amtriangle' },
      envelope: { attack: 0.005, decay: 0.1, sustain: 0.1, release: 0.2 }
    }).connect(effectBus)

    ToneActual.getDestination().volume.value = ToneActual.gainToDb(enabled ? volume : 0)
    
    // Ensure transport is running for our scheduling
    ToneActual.getTransport().start()
    
    initialized = true
    
    if (desiredAmbientScene) startAmbient(desiredAmbientScene)
  })()

  try {
    await toneStartPromise
  } finally {
    toneStartPromise = null
  }
}

// --- API ---

export function initAudio(): void {
  if (typeof window === 'undefined') return
  enabled = readEnabled()
  volume = readVolume()
  if (initListenersBound) return
  initListenersBound = true

  const unlock = () => {
    audioUnlocked = true
    window.removeEventListener('click', unlock, true)
    window.removeEventListener('keydown', unlock, true)
    window.removeEventListener('touchstart', unlock, true)
    window.removeEventListener('pointerdown', unlock, true)
  }

  window.addEventListener('click', unlock, true)
  window.addEventListener('keydown', unlock, true)
  window.addEventListener('touchstart', unlock, true)
  window.addEventListener('pointerdown', unlock, true)

  // AUDIO-BUG-01: Resume on visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && initialized) {
      ToneActual.getContext().resume()
    }
  })
}

export function playEffect(name: SoundEffect): void {
  if (!enabled) return
  if (!initialized || !sfxSynth) {
    if (audioUnlocked) {
      void ensureToneStarted(`playEffect:${name}`).then(() => {
        if (initialized && sfxSynth) playEffect(name)
      })
    }
    return
  }

  const now = ToneActual.now()
  switch (name) {
    case 'ui_click':
      sfxSynth.triggerAttackRelease(880, '16n', now)
      break
    case 'ui_open':
      sfxSynth.triggerAttackRelease(['440', '660'], '8n', now)
      break
    case 'ui_close':
      sfxSynth.triggerAttackRelease(['660', '440'], '8n', now)
      break
    case 'action_success':
      sfxSynth.triggerAttackRelease(['C5', 'E5', 'G5'], '4n', now)
      break
    case 'action_fail':
      sfxSynth.triggerAttackRelease('G2', '2n', now)
      break
    case 'level_up':
      sfxSynth.triggerAttackRelease(['C4', 'E4', 'G4', 'C5'], '2n', now)
      break
    default:
      sfxSynth.triggerAttackRelease(440, '8n', now)
  }
}

export function playAmbient(scene: AmbientScene | string): void {
  const resolved = resolveAmbientScene(scene)
  desiredAmbientScene = resolved
  if (!enabled) return
  if (!initialized) { ensureToneStarted('playAmbient'); return }
  if (currentAmbientScene === resolved) return

  startAmbient(resolved)
}

let ambientSignatureKey = ''
let ambientSignature = 0

// --- Ambient Nodes ---
let ambientNoise: ToneActual.Noise | null = null
let ambientFilter: ToneActual.AutoFilter | null = null
let ambientDrones: ToneActual.Oscillator[] = []
let ambientCleanupTimer: ReturnType<typeof setTimeout> | null = null

function hashText(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function startAmbient(scene: AmbientScene) {
  if (!ambientBus) return
  
  const sig = ambientSignature
  const toneShift = ((sig % 9) - 4) * 2
  
  // AUDIO-BUG-03: Robust cross-fade and cleanup
  if (ambientCleanupTimer) {
    clearTimeout(ambientCleanupTimer)
    ambientCleanupTimer = null
  }

  const oldDrones = [...ambientDrones]
  const oldNoise = ambientNoise
  const oldFilter = ambientFilter
  
  if (oldDrones.length || oldNoise) {
    oldDrones.forEach(d => d.volume.rampTo(-Infinity, 2))
    if (oldNoise) oldNoise.volume.rampTo(-Infinity, 2)
    
    // Explicitly local cleanup to avoid racing with new ambient
    setTimeout(() => {
      oldDrones.forEach(d => { try { d.stop(); d.dispose() } catch {} })
      if (oldNoise) { try { oldNoise.stop(); oldNoise.dispose() } catch {} }
      if (oldFilter) { try { oldFilter.dispose() } catch {} }
    }, 2100)
  }

  currentAmbientScene = scene
  ambientBus.gain.rampTo(0.12, 2)

  ambientDrones = []
  // 1. Drones based on scene
  const droneCount = 2 + (sig % 2)
  for (let i = 0; i < droneCount; i++) {
    const osc = new ToneActual.Oscillator({
      frequency: 60 + (i * 40) + toneShift,
      type: (sig + i) % 2 === 0 ? 'triangle' : 'sine',
      volume: -Infinity
    }).connect(ambientBus)
    
    const lfo = new ToneActual.LFO(0.05 + (i * 0.02), 0.5, 2).start()
    lfo.connect(osc.volume)
    
    osc.start()
    osc.volume.rampTo(-24, 2)
    ambientDrones.push(osc)
  }

  // 2. Scene-specific noise/texture
  ambientNoise = new ToneActual.Noise({
    type: scene === 'forest' || scene === 'wilderness' ? 'pink' : 'brown',
    volume: -Infinity
  })

  ambientFilter = new ToneActual.AutoFilter({
    frequency: 0.1,
    baseFrequency: scene === 'dungeon' || scene === 'cave' ? 200 : 800,
    octaves: 2.5
  }).connect(ambientBus).start()

  ambientNoise.connect(ambientFilter)
  ambientNoise.start()
  ambientNoise.volume.rampTo(-30, 3)
}

function stopAmbientInternal() {
  // AUDIO-BUG-06: Only stop if we actually want it stopped
  if (desiredAmbientScene !== null) return

  ambientDrones.forEach(d => { try { d.stop(); d.dispose() } catch {} })
  ambientDrones = []
  if (ambientNoise) { try { ambientNoise.stop(); ambientNoise.dispose() } catch {} ambientNoise = null }
  if (ambientFilter) { try { ambientFilter.dispose() } catch {} ambientFilter = null }
}

export function setAmbientSignature(worldSeed = '', locationText = ''): boolean {
  const nextKey = `${worldSeed}::${locationText}`
  if (nextKey === ambientSignatureKey) return false
  ambientSignatureKey = nextKey
  ambientSignature = hashText(nextKey || 'adventure-machine::ambient')
  
  if (currentAmbientScene && initialized) {
    startAmbient(currentAmbientScene)
  }
  return true
}

export function stopAmbient(): void {
  desiredAmbientScene = null
  currentAmbientScene = null
  ambientBus?.gain.rampTo(0, 2)
  
  if (ambientCleanupTimer) clearTimeout(ambientCleanupTimer)
  ambientCleanupTimer = setTimeout(() => stopAmbientInternal(), 2200)
}

export function setVolume(level: number): void {
  volume = Math.max(0, Math.min(1, level))
  if (initialized) {
    ToneActual.getDestination().volume.rampTo(ToneActual.gainToDb(enabled ? volume : 0), 0.1)
  }
  try { localStorage.setItem(STORAGE_VOLUME, String(volume)) } catch {}
}

export function toggleAudio(): boolean {
  enabled = !enabled
  if (enabled && audioUnlocked && !initialized) {
    void ensureToneStarted('toggleAudio')
  }
  if (initialized) {
    ToneActual.getDestination().volume.rampTo(ToneActual.gainToDb(enabled ? volume : 0), 0.1)
  }
  try { localStorage.setItem(STORAGE_ENABLED, String(enabled)) } catch {}
  return enabled
}

export function isAudioEnabled(): boolean { return enabled }
export function getVolume(): number { return volume }

// --- Scoring ---

export function setScoreMood(mood: ScoreMood | null): void {
  if (scoreMood === mood) return
  
  // Duck ambient slightly when score starts
  if (mood && !scoreMood && ambientBus) {
    ambientBus.gain.rampTo(0.06, 1.5)
  } else if (!mood && ambientBus) {
    ambientBus.gain.rampTo(0.12, 2)
  }

  scoreMood = mood
  if (nextScoreTransportId !== null) {
    ToneActual.getTransport().clear(nextScoreTransportId)
    nextScoreTransportId = null
  }

  if (!mood || !initialized) return
  
  playScorePhrase()
}

function playScorePhrase() {
  if (!scoreMood || !initialized || !melodySynth || !bassSynth || !harmonySynth) return

  const phrases = dynamicScores.get(scoreMood) || []
  if (phrases.length === 0) return

  let idx = Math.floor(Math.random() * phrases.length)
  if (phrases.length > 1 && idx === lastScorePhraseIndex) idx = (idx + 1) % phrases.length
  lastScorePhraseIndex = idx

  const phrase = phrases[idx]
  const now = ToneActual.now()

  phrase.notes.forEach(n => {
    const time = now + n.at
    if (n.hz < 160) {
      bassSynth?.triggerAttackRelease(n.hz, n.duration, time, n.gain * 1.2)
    } else if (n.hz > 550) {
      melodySynth?.triggerAttackRelease(n.hz, n.duration, time, n.gain)
    } else {
      harmonySynth?.triggerAttackRelease(n.hz, n.duration, time, n.gain * 0.8)
    }
  })

  // AUDIO-BUG-02: Use Tone.Transport for better drift-free scheduling
  const nextDelaySec = (phrase.durationMs / 1000) + 3 + (Math.random() * 4)
  nextScoreTransportId = ToneActual.getTransport().scheduleOnce(() => {
    playScorePhrase()
  }, `+${nextDelaySec}`)
}

export function registerDynamicScore(mood: ScoreMood, notes: ToneSpec[], durationMs: number): void {
  const existing = dynamicScores.get(mood) || []
  existing.push({ notes, durationMs })
  dynamicScores.set(mood, existing)
}

export function clearDynamicScores(): void {
  dynamicScores.clear()
}

// --- Helpers ---

function resolveAmbientScene(scene: AmbientScene | string): AmbientScene {
  const v = String(scene || '').toLowerCase().trim()
  if (v === 'forest' || v === 'tavern' || v === 'city' || v === 'wilderness' || v === 'dungeon' || v === 'cave' || v === 'ocean' || v === 'void') return v as AmbientScene
  if (v.includes('inn') || v.includes('pub')) return 'tavern'
  if (v.includes('mine') || v.includes('tunnel')) return 'cave'
  return 'wilderness'
}

export function inferAmbientScene(roomDesc: string): AmbientScene {
  return resolveAmbientScene(roomDesc)
}

export function inferScoreMood(context: { inCombat?: boolean; lowHealth?: boolean; newRoom?: boolean; questComplete?: boolean }): ScoreMood {
  if (context.inCombat) return 'combat'
  if (context.questComplete) return 'triumph'
  if (context.newRoom) return 'discovery'
  if (context.lowHealth) return 'sorrow'
  return 'calm'
}

export function __resetAudioForTests(): void {
  initialized = false
  audioUnlocked = false
  const dest = ToneActual.getDestination()
  if (dest && dest.volume) {
    dest.volume.value = -Infinity
  }
}

export function __getAudioDebugState() {
  return { enabled, initialized, currentAmbientScene, desiredAmbientScene, lastStartReason }
}
